import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';

export default function DressersPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Dressers - Bedroom Decor - Interior Decor Solutions',
    url: `${siteUrl}/bedroom/dressers`,
    description: 'Explore dressers that offer ample storage without compromising on style. From tall chests to wide dressers, find pieces that organize your wardrobe beautifully.',
    publisher: {
      '@type': 'Organization',
      name: 'Interior Decor Solutions',
    },
  };

  const products = [
    {
      id: 1,
      name: 'Modern 6-Drawer Dresser',
      image: 'https://public.readdy.ai/ai/img_res/a8f56c8c28b87e365a147344f4e18652.jpg',
      rating: 4.8,
      reviews: 156,
      description: 'Transform your bedroom with this elegant 6-drawer dresser that offers ample storage while maintaining a sleek, modern aesthetic perfect for contemporary spaces.',
      features: [
        'Six spacious drawers with soft-close mechanism',
        'Premium engineered wood construction',
        'Modern white finish with chrome handles',
        'Anti-tip safety hardware included',
        'Dimensions: 58"W x 18"D x 32"H'
      ]
    },
    {
      id: 2,
      name: 'LIKIMIO 54" Tall 5 Drawer Dresser',
      image: 'https://static.readdy.ai/image/2c7c87ff35994995d31b3af183ddcbb3/172a64081afc6413d554b96e416404e7.jpeg',
      rating: 4.7,
      reviews: 189,
      description: 'This contemporary tall dresser features handle-free drawer design with RGB LED lighting and built-in charging station, perfect as a modern bedroom storage organizer or living room clothes dresser.',
      features: [
        'Multi-color RGB LED lighting with remote control',
        'Built-in charging station with USB-C and AC outlets',
        'Five large capacity drawers for clothes storage',
        'Anti-tipping belt for enhanced stability'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Dressers - Bedroom Decor - Interior Decor Solutions"
        description="Explore dressers that offer ample storage without compromising on style. From tall chests to wide dressers, find pieces that organize your wardrobe beautifully."
        keywords="dressers, bedroom storage, chest of drawers, tall dresser, bedroom furniture, wardrobe storage"
        canonical={`${siteUrl}/bedroom/dressers`}
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="px-4 md:px-8 lg:px-20 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <h1 className="text-[#1A4D4D] text-xl md:text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
              Interior Decor Solutions
            </h1>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Home</Link>
            <Link to="/blog" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Blog</Link>
            <Link to="/room-inspiration" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Room Inspiration</Link>
            <Link to="/reviews" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Reviews</Link>
            <Link to="/about" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">About</Link>
          </div>

          <button className="hidden lg:flex bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap items-center gap-2">
            <i className="ri-mail-line"></i>
            Get Ideas
          </button>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A4D4D]"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block text-[#1A4D4D] font-medium text-base py-2">Home</Link>
              <Link to="/blog" className="block text-[#1A4D4D] font-medium text-base py-2">Blog</Link>
              <Link to="/room-inspiration" className="block text-[#1A4D4D] font-medium text-base py-2">Room Inspiration</Link>
              <Link to="/reviews" className="block text-[#1A4D4D] font-medium text-base py-2">Reviews</Link>
              <Link to="/about" className="block text-[#1A4D4D] font-medium text-base py-2">About</Link>
              <button className="w-full bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-mail-line"></i>
                Get Ideas
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden mt-[70px]">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=elegant%20bedroom%20with%20beautiful%20dresser%20multiple%20drawers%20organized%20storage%20contemporary%20furniture%20quality%20craftsmanship%20peaceful%20atmosphere%20modern%20design%20functional%20bedroom%20piece&width=1920&height=1080&seq=dressers-hero&orientation=landscape"
            alt="Dressers"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-20 text-center">
          <div className="mb-4">
            <Link to="/bedroom" className="text-white/80 hover:text-white text-sm md:text-base">
              <i className="ri-arrow-left-line mr-2"></i>Back to Bedroom
            </Link>
          </div>
          <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-light mb-4 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Dressers
          </h1>
          <p className="text-white/90 text-base md:text-lg max-w-2xl mx-auto">
            Organize beautifully with style
          </p>
        </div>
      </section>

      {/* Products Section */}
      {products.map((product, index) => (
        <section 
          key={product.id}
          className={`py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Image */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="w-full h-[400px] md:h-[500px] rounded-3xl overflow-hidden shadow-lg">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain object-center hover:scale-105 transition-transform duration-500 bg-white"
                  />
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <h2 className="text-[#1A4D4D] text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                  {product.name}
                </h2>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i 
                        key={i} 
                        className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-[#FF6B6B] text-lg`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-gray-600 text-sm">
                    {product.rating} ({product.reviews} reviews)
                  </span>
                </div>

                <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-6">
                  {product.description}
                </p>

                <div className="mb-8">
                  <h3 className="text-[#1A4D4D] text-lg font-semibold mb-4">Key Features:</h3>
                  <ul className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <i className="ri-checkbox-circle-fill text-[#1A4D4D] mt-1"></i>
                        <span className="text-sm md:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link 
                  to="/reviews"
                  className="inline-flex items-center gap-2 bg-[#1A4D4D] text-white px-8 py-3 rounded-full font-medium hover:bg-[#0F3333] transition-all whitespace-nowrap text-sm md:text-base"
                >
                  View Full Review <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20 bg-[#1A4D4D]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Explore More Bedroom Essentials
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8">
            Complete your bedroom with our curated collection
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/bedroom"
              className="bg-white text-[#1A4D4D] px-8 py-3 rounded-full font-medium hover:bg-gray-100 transition-all whitespace-nowrap text-sm md:text-base"
            >
              Back to Bedroom
            </Link>
            <Link 
              to="/reviews"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-medium hover:bg-white/10 transition-all whitespace-nowrap text-sm md:text-base"
            >
              All Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-[#1A4D4D] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="home-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M40 20 L50 30 L50 45 L30 45 L30 30 Z" stroke="white" fill="none" strokeWidth="1"/>
              <circle cx="15" cy="60" r="3" stroke="white" fill="none" strokeWidth="1"/>
              <rect x="60" y="55" width="8" height="8" stroke="white" fill="none" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#home-pattern)"/>
          </svg>
        </div>

        <div className="relative z-10 px-4 md:px-8 lg:px-20 py-8 md:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-10 gap-8 md:gap-10">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/9e46e3d9-adac-48a0-871c-356cdcae054d.png" alt="Simple Home Decor" className="h-8 w-auto brightness-0 invert" />
              </div>
              
              <p className="text-white text-2xl md:text-3xl font-light leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                Simple touches.<br />
                Cozy homes.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Shop</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/living-room" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Living Room</Link></li>
                  <li><Link to="/bedroom" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Bedroom</Link></li>
                  <li><Link to="/kitchen" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Kitchen</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Learn</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Blog</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Reviews</Link></li>
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Guides</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Company</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/about" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">About</Link></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Contact</a></li>
                  <li><Link to="/affiliate-disclosure" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Affiliate Disclosure</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Connect</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Pinterest</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Instagram</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Newsletter</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/20 px-4 md:px-8 lg:px-20 py-6 md:py-8">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/80 text-xs md:text-sm text-center md:text-left">
              © 2026 Simple Home Decor. All rights reserved.
            </p>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-pinterest-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-instagram-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-facebook-line text-base md:text-lg"></i>
              </a>
              <a href="https://www.youtube.com/@InteriorDecorSolutions" target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-youtube-line text-base md:text-lg"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-white/80 text-xs md:text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</Link>
              <span className="hidden md:inline">|</span>
              <Link to="/terms" className="hover:text-white transition-colors whitespace-nowrap">Terms of Use</Link>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto mt-6 pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs text-center leading-relaxed">
              Affiliate Disclosure: This website contains affiliate links. As an Amazon Associate, we earn from qualifying purchases at no additional cost to you.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
